import React, { useState } from "react";
import UserListSidebar from "./UserListSidebar";
import WhatsappChat from "./WhatsappChat";
import TemplateMessagesView from "./TemplateMessagesView";
import { useWhatsapp } from "./WhatsappContext";

type Tab = "chat" | "templates";

const WhatsAppPage: React.FC = () => {
    const {
        users,
        selectedUser,
        whatsappMessages,
        selectUser,
        clearSelectedUser,
        sendMessage,
        sendWhatsappTemplate,
        refreshUsers,
        templates,
        fetchTemplates,
        unreadSummary,
        fetchUnreadSummary,
        socketError,
        clearSocketError,
        usersLoading,
        usersHasMore,
        userSearch,
        searchUsers,
        loadMoreUsers,
    } = useWhatsapp();

    const [activeTab, setActiveTab] = useState<Tab>("chat");

    return (
        <div className="flex flex-col h-[calc(100vh-6rem)] bg-slate-200 dark:bg-slate-900 rounded-2xl overflow-hidden shadow-2xl relative">
            {socketError && (
                <div className="absolute inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="w-full max-w-md rounded-2xl border border-red-500/40 bg-slate-900 shadow-2xl p-6">
                        <h3 className="text-lg font-bold text-red-300 mb-2">{socketError.title}</h3>
                        <p className="text-sm text-slate-200 mb-4">{socketError.message}</p>
                        {socketError.code && (
                            <p className="text-xs text-slate-400 mb-4">Code: {socketError.code}</p>
                        )}
                        <div className="flex justify-end">
                            <button
                                onClick={clearSocketError}
                                className="px-4 py-2 text-sm font-semibold bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors"
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex flex-1 relative overflow-hidden">
                {/* Sidebar — always visible */}
                <div className={`${selectedUser ? 'hidden md:flex' : 'flex'} w-full md:w-auto h-full z-10`}>
                    <UserListSidebar
                        users={users}
                        onSelect={(user) => { selectUser(user); setActiveTab("chat"); }}
                        selectedUser={selectedUser}
                        onSendTemplate={sendWhatsappTemplate}
                        onRefresh={refreshUsers}
                        templates={templates}
                        onFetchTemplates={fetchTemplates}
                        unreadSummary={unreadSummary}
                        onFetchUnreadSummary={fetchUnreadSummary}
                        usersLoading={usersLoading}
                        usersHasMore={usersHasMore}
                        searchTerm={userSearch}
                        onSearch={searchUsers}
                        onLoadMore={loadMoreUsers}
                    />
                </div>

                {/* Right panel */}
                {selectedUser ? (
                    <div className={`${selectedUser ? 'flex' : 'hidden md:flex'} flex-1 flex-col h-full bg-slate-950 relative z-0`}>
                        {/* Tab bar inside the chat window area */}
                        <div className="flex items-center gap-1 px-4 py-2 bg-slate-900 border-b border-slate-800 shrink-0">
                            <button
                                onClick={() => setActiveTab("chat")}
                                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                    activeTab === "chat"
                                        ? "bg-blue-600/20 text-blue-300 border border-blue-500/30"
                                        : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"
                                }`}
                            >
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                </svg>
                                Chat
                            </button>
                            <button
                                onClick={() => setActiveTab("templates")}
                                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                    activeTab === "templates"
                                        ? "bg-blue-600/20 text-blue-300 border border-blue-500/30"
                                        : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"
                                }`}
                            >
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Templates
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-hidden">
                            {activeTab === "chat" ? (
                                <WhatsappChat
                                    messages={whatsappMessages}
                                    onSendMessage={sendMessage}
                                    user={selectedUser}
                                    onBack={clearSelectedUser}
                                />
                            ) : (
                                <TemplateMessagesView waId={selectedUser.wa_id} />
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="hidden md:flex flex-1 items-center justify-center bg-[#efeae2] dark:bg-slate-950/50 border-l border-slate-800">
                        <div className="text-center p-8">
                            <div className="bg-slate-900 p-6 rounded-full inline-flex mb-6 shadow-xl">
                                <span className="text-6xl">💬</span>
                            </div>
                            <h2 className="text-3xl font-bold text-slate-700 dark:text-gray-200 mb-3">
                                WhatsApp Admin
                            </h2>
                            <p className="text-gray-500 dark:text-gray-400 max-w-md">
                                Select a conversation from the sidebar to start chatting.
                                <br />
                                <span className="text-sm opacity-70 mt-2 block">
                                    Stay connected with your users directly from here.
                                </span>
                            </p>
                            <div className="mt-8 flex justify-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0s' }}></div>
                                <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WhatsAppPage;
